/**
 * StorybookPage Component
 *
 * Single page of the storybook with illustration and text.
 */

import { Loader2, ImageOff, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getUploadUrl } from '@/integrations/api/client';
import type { StoryPage } from '@/services/storyPages';

const KB_VARIANTS = [
  'kb-zoom-in',
  'kb-pan-right',
  'kb-zoom-out',
  'kb-pan-left',
  'kb-zoom-in-corner',
  'kb-zoom-out-corner',
] as const;

interface StorybookPageProps {
  page: StoryPage;
  pageNumber: number;
  totalPages: number;
  isActive: boolean;
  /** Index of the word currently being narrated (-1 = none). */
  activeWordIndex?: number;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

export function StorybookPage({
  page,
  pageNumber,
  totalPages,
  isActive,
  activeWordIndex = -1,
  onRegenerate,
  isRegenerating,
}: StorybookPageProps) {
  // Split into words for narration highlighting. Must match the tokenisation in
  // StorybookAudioControls (text.split(/\s+/).filter(Boolean)) so indices align.
  const words = (page.content || '').split(/\s+/).filter(Boolean);
  const illustrationUrl = page.illustration_url
    ? getUploadUrl(page.illustration_url)
    : null;

  return (
    <div
      className={cn(
        'relative w-full h-full rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-b from-primary/10 to-primary/10',
        'transition-opacity duration-300',
        isActive ? 'opacity-100' : 'opacity-0'
      )}
    >
      {/* Page number badge */}
      <div className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm rounded-full px-3 py-1 text-body-sm font-medium text-foreground/70 z-10">
        {pageNumber} / {totalPages}
      </div>

      {/* Vertically centered content: image + text stacked */}
      <div className="w-full h-full overflow-y-auto flex flex-col justify-center">
        {/* Illustration */}
        {page.illustration_status === 'completed' && illustrationUrl ? (
          <div key={pageNumber} className="w-full overflow-hidden">
            <img
              src={illustrationUrl}
              alt={`Illustration for page ${pageNumber}`}
              className={cn('w-full block', KB_VARIANTS[(pageNumber - 1) % KB_VARIANTS.length])}
              style={{ transformOrigin: 'center center' }}
            />
          </div>
        ) : page.illustration_status === 'generating' || page.illustration_status === 'pending' ? (
          <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-primary/20 to-lolo/10">
            <div className="text-center text-primary">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-2" />
              <p className="text-body-sm">Creating illustration...</p>
            </div>
          </div>
        ) : (
          <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/30">
            <div className="text-center text-muted-foreground px-4">
              <ImageOff className="h-12 w-12 mx-auto mb-2" />
              <p className="text-body-sm mb-3">Illustration unavailable</p>
              {onRegenerate && (
                <button
                  onClick={onRegenerate}
                  disabled={isRegenerating}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-body-sm font-medium hover:opacity-90 disabled:opacity-60 transition"
                >
                  {isRegenerating ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Trying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3.5 w-3.5" />
                      Try again
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Text directly below image */}
        <div className="px-3 py-3 md:px-5 md:py-4">
          <div className="bg-black/40 backdrop-blur-md rounded-2xl p-3 md:p-4 max-w-prose mx-auto">
            <p className="text-body-sm md:text-body lg:text-body-lg leading-relaxed text-white/90 font-serif text-center drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              {words.map((word, i) => (
                <span
                  key={i}
                  className={cn(
                    'transition-colors duration-150 rounded px-0.5',
                    i === activeWordIndex && 'bg-primary/70 text-white'
                  )}
                >
                  {word}
                  {i < words.length - 1 ? ' ' : ''}
                </span>
              ))}
            </p>
          </div>
        </div>
      </div>

      {/* Decorative book spine effect */}
      <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-black/10 to-transparent z-10" />

      {/* Page edge effect */}
      <div className="absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-l from-black/5 to-transparent z-10" />
    </div>
  );
}
